import { useContext } from "react"

// Context
import { UserContext } from "../../../context/userContext"

//Referral components
import { refApiRefEntityNotePut } from "../ReferralsAPI"
import { getNotePayload } from "./sharedFunctions"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"

//Formik
import { Formik, Form } from "formik"
import * as Yup from "yup"
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"
import FormikTextAreaInput from "../../../helpers/formikInputs/FormikTextAreaInput"

//GraphQL
import { useMutation } from "@apollo/client"
import { UPDATE_REFERRAL_STATUS } from "../queries"
import { LogError } from "../../../helpers/logger"

export default function ReferralStatusPopup({
  showForm,
  onHideForm,
  onStatusChanged,
  referralOrganizationId,
  referralIdStamp,
  referralStatus,
  referralParticipantsNotes,
  referralEntityId,
}) {
  const userCtx = useContext(UserContext)

  const referralStatuses = [
    "Active referral",
    "Completed referral",
    "Incomplete referral",
  ]

  const [updateReferral] = useMutation(UPDATE_REFERRAL_STATUS, {
    variables: {
      referral_status: "",
      referral_participants_notes: "",
      user_type: "",
      idStamp: "",
    },
  })

  const validationSchema = Yup.object().shape({
    status: Yup.string()
      .required("Please specify a status to update")
      .notOneOf(
        [referralStatus],
        "The status is unchanged, please select a new status"
      ),
    note: Yup.string().required("Please enter a note for the status update"),
  })

  const handleSubmit = async (values) => {
    try {
      const referral_update = await updateReferral({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          organization_id: referralOrganizationId,
          referral_status: values.status,
          referral_participants_notes: referralParticipantsNotes,
          user_type: "staff",
          idStamp: referralIdStamp,
        },
      })

      if (!!referral_update.data.updateBlockReferral) {
        //Add status update note to Referral service
        const payload = {
          userId: userCtx.session.user.staff_id,
          note: values.note,
          referralId: referralIdStamp,
          referralEntityId: referralEntityId,
          notePayload: getNotePayload(userCtx),
          ReferralStatusOld: referralStatus,
          ReferralStatusNew: values.status,
        }
        const referralNoteResult = await refApiRefEntityNotePut(
          userCtx.token,
          values.id,
          payload
        )

        if (!referralNoteResult) {
          LogError("Unable to submit status update note", referral_update)
          userCtx.setNotificationError("Unable to submit status update note")
        }

        onStatusChanged(values.status)
      } else {
        LogError("Unable to submit status update", referral_update)
        userCtx.setNotificationError("Unable to submit status update")
        return false
      }
    } catch (error) {
      LogError("Unable to submit status update", error)
      userCtx.setNotificationError("Unable to submit status update")
      return false
    }
  }

  return (
    <Dialog
      header={"Referral status update"}
      visible={showForm}
      onHide={() => {
        return onHideForm()
      }}
      style={{ width: "30vw", height: "465px" }}
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          status: referralStatus,
          note: "",
        }}
        enableReinitialize
        onSubmit={async (values) => {
          return await handleSubmit(values)
        }}
      >
        {({ handleSubmit, isValid, isSubmitting, dirty, values, errors }) => (
          <Form onSubmit={handleSubmit}>
            <FormikDropDown
              name="status"
              label="Referral status"
              helpText={"Please select a new status"}
              placeholderText="Partner status"
              options={referralStatuses}
              required={true}
              disabled={isSubmitting}
            />
            <div className="p-fluid" style={{ height: "200px" }}>
              <FormikTextAreaInput
                label={"Status change note"}
                helpText={"Please add a note for this status update. "}
                name="note"
                placeholderText="Note"
                required={true}
                rows={4}
                disabled={isSubmitting}
              />
            </div>
            <div style={{ float: "right" }}>
              <Button
                className="btn"
                label="Submit"
                type="submit"
                disabled={!dirty}
                loading={isSubmitting}
              />
              <Button
                className="btn"
                label="Cancel"
                style={{ marginLeft: "10px" }}
                type="button"
                onClick={() => {
                  onHideForm()
                }}
                disabled={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
